<script lang="ts">
	import type { ActionReturn } from "svelte/action";
	import Portal from "svelte-portal";

	let className = "";
	export { className as class };
	export let intersectionThreshold = 0.75;
	let tooltip = null as HTMLElement | null;

	let tooltipHovered = false;
	let elementVisibleEnough = true;
	let isInverted = false;

	function getNearestScrollableElement(element: HTMLElement | null): HTMLElement | Document | null {
		let currentElement = element;

		while (currentElement) {
			const computedStyle = window.getComputedStyle(currentElement);

			// Check if the element is scrollable
			const { overflowY } = computedStyle;
			const { overflowX } = computedStyle;
			const isScrollable =
				overflowY === "scroll" || overflowY === "auto" || overflowX === "scroll" || overflowX === "auto";

			if (
				isScrollable &&
				(currentElement.scrollHeight > currentElement.clientHeight ||
					currentElement.scrollWidth > currentElement.clientWidth)
			) {
				if (currentElement === document.documentElement) {
					return document;
				} else {
					return currentElement;
				}
			}

			// Move up to the parent element
			currentElement = currentElement.parentElement;
		}

		// If no scrollable element is found, return null
		return null;
	}

	function handleTooltip(element: HTMLElement): ActionReturn {
		let currentScrollElement: HTMLElement | Document | null = null;

		function updateTooltipSizeAndPosition(): void {
			if (!tooltip) {
				return;
			}

			const tooltipRect = tooltip.getBoundingClientRect();
			const rect = element.getBoundingClientRect();
			isInverted = rect.top + tooltipRect.height > window.innerHeight;
			tooltip.style.left = `${rect.left}px`;
			if (isInverted) {
				tooltip.style.top = "";
				tooltip.style.bottom = `${window.innerHeight - rect.top}px`;
			} else {
				tooltip.style.top = `${rect.bottom}px`;
				tooltip.style.bottom = "";
			}
		}

		function checkToHide(): void {
			if (element.matches(":hover")) {
				return;
			}
			if (tooltip?.matches(":hover")) {
				return;
			}

			tooltipHovered = false;
			currentScrollElement?.removeEventListener("scroll", updateTooltipSizeAndPosition);
			currentScrollElement?.removeEventListener("scroll", checkToHide);
			currentScrollElement = null;
		}

		function show(): void {
			tooltipHovered = true;

			currentScrollElement = getNearestScrollableElement(element);
			currentScrollElement?.addEventListener("scroll", updateTooltipSizeAndPosition);
			currentScrollElement?.addEventListener("scroll", checkToHide);

			updateTooltipSizeAndPosition();
		}

		const observer = new IntersectionObserver(
			(entries) => {
				elementVisibleEnough = entries.some((entry) => entry.isIntersecting);
			},
			{ threshold: intersectionThreshold },
		);
		observer.observe(element);

		element.addEventListener("mouseenter", show);
		document.addEventListener("mousemove", checkToHide);

		return {
			destroy(): void {
				observer.disconnect();
				element.removeEventListener("mouseenter", show);
				document.removeEventListener("mousemove", checkToHide);
			},
		};
	}

	$: shown = tooltipHovered && elementVisibleEnough;
</script>

<div use:handleTooltip class={className}>
	<slot />
</div>

{#if $$slots.tooltip}
	<Portal target="body">
		<div
			bind:this={tooltip}
			role="none"
			class="fixed z-[1000] transition-opacity"
			class:pt-3={!isInverted}
			class:pb-3={isInverted}
			class:opacity-0={!shown}
			class:pointer-events-none={!shown}
			on:click={(event) => {
				if (!(event.target instanceof HTMLAnchorElement)) {
					event.preventDefault();
				}
			}}
		>
			<div
				class="absolute left-[calc(.75rem-1px)] border-l-[.5625rem] border-r-[.5625rem] border-transparent"
				class:border-b-[.8125rem]={!isInverted}
				class:border-t-[.8125rem]={isInverted}
				class:border-b-secondary-250={!isInverted}
				class:border-t-secondary-250={isInverted}
				class:top-0={!isInverted}
				class:bottom-0={isInverted}
			></div>
			<div
				class="absolute left-3 border-l-[.5rem] border-r-[.5rem] border-transparent"
				class:border-b-[.75rem]={!isInverted}
				class:border-t-[.75rem]={isInverted}
				class:border-b-secondary-100={!isInverted}
				class:border-t-secondary-100={isInverted}
				class:top-px={!isInverted}
				class:bottom-px={isInverted}
			></div>
			<div
				class="border-1 border-secondary-250 bg-secondary-100 cursor-default rounded-lg px-5 pb-4 pt-[1.375rem] text-xs"
			>
				<slot name="tooltip" />
			</div>
		</div>
	</Portal>
{/if}
